import { useCallback, useState } from "react";
import useSWR from "swr";

import {
  createProvision,
  deleteProvision,
  listProvisions,
  PROVISIONS_KEY,
  updateProvision,
  type CreateProvisionPayload,
  type UpdateProvisionPayload,
} from "@/features/provisions/api/provisionsApi";
import type { Provision } from "@/features/provisions/types";
import { revalidate, toErrorMessage } from "@/lib/swr";

const EMPTY: Provision[] = [];

/** A provision feeds the recipes that use it and the stock it was bought into. */
const TOUCHED_CACHES = [PROVISIONS_KEY, "recipes", "provision-stocks/"];

export function useProvisions() {
  const { data, error, isLoading, mutate } = useSWR(PROVISIONS_KEY, listProvisions);

  return {
    provisions: data ?? EMPTY,
    isLoading,
    error: toErrorMessage(error),
    refresh: mutate,
  };
}

export function useCreateProvision() {
  const { mutate } = useSWR(PROVISIONS_KEY, listProvisions);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCallback(
    async (payload: CreateProvisionPayload) => {
      setIsCreating(true);
      setError(null);
      try {
        const created = await createProvision(payload);
        await mutate();
        return created;
      } catch (e) {
        setError(toErrorMessage(e));
        throw e;
      } finally {
        setIsCreating(false);
      }
    },
    [mutate],
  );

  return { create, isCreating, error };
}

export function useUpdateProvision() {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = useCallback(async (id: string, payload: UpdateProvisionPayload) => {
    setIsUpdating(true);
    setError(null);
    try {
      const updated = await updateProvision(id, payload);
      await revalidate(...TOUCHED_CACHES);
      return updated;
    } catch (e) {
      setError(toErrorMessage(e));
      throw e;
    } finally {
      setIsUpdating(false);
    }
  }, []);

  return { update, isUpdating, error };
}

export function useDeleteProvision() {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = useCallback(async (id: string) => {
    setIsDeleting(true);
    setError(null);
    try {
      const deleted = await deleteProvision(id);
      await revalidate(...TOUCHED_CACHES);
      return deleted;
    } catch (e) {
      setError(toErrorMessage(e));
      throw e;
    } finally {
      setIsDeleting(false);
    }
  }, []);

  return { remove, isDeleting, error };
}
